/**
 * ESS (Experiences Storage Service) handlers for project + comic persistence.
 *
 * Projects and comics are independent entities. loadESSProject restores only
 * the story; comics are view-only from the gallery.
 */

import type { Socket } from "socket.io-client";
import { useStoryStore } from "../storyStore";
import { useSocketStore } from "../socketStore";
import { useESSStore } from "../essStore";
import { usePreviewStore } from "../previewStore";
import { useComicStore } from "../comicStore";

import { useConfigStore } from "../configStore";
import { confirmDeleted, recordDeleteFailure } from "../galleryDeleteToast";
import { GENERIC_ERROR, STORY_NOT_FOUND_ERROR } from "../errorMessages";
import { DEFAULT_IMAGE_STYLE, DEFAULT_OUTPUT_FORMAT } from "@amzn/a4k-creator-studio-shared";
import minervaService from "../../services/minervaService";
import { AppEvent } from "../../services/appEvents";

export function registerESSHandlers(socket: Socket) {
  socket.on("essProjectList", (data: { projects: any[]; nextToken?: string; isAppend?: boolean }) => {
    if (data.isAppend) {
      useESSStore.getState().appendProjects(data.projects, data.nextToken);
    } else {
      useESSStore.getState().setProjects(data.projects, data.nextToken);
    }
  });

  socket.on("essComicList", (data: { comics: any[]; nextToken?: string; isAppend?: boolean }) => {
    if (data.isAppend) {
      useESSStore.getState().appendComics(data.comics, data.nextToken);
    } else {
      useESSStore.getState().setComics(data.comics, data.nextToken);
    }
  });

  socket.on("essProjectLoaded", (data: { storyId: string; story: any; imageStyle?: string; outputFormat?: string; comic?: any }) => {
    useStoryStore.getState().restoreStory(data.story);
    // A comic is present only when the loaded project has a still-generating book
    // (its detached generation keeps streaming progress to this socket). Restore
    // it and rehydrate the pages already imaged before this load — the payload is
    // image-stripped and live emits only cover pages drawn from here on, so
    // without this the completed pages would render blank.
    if (data.comic) {
      useComicStore.getState().replaceAll(data.comic);
    } else {
      useComicStore.getState().clearAll();
    }
    useConfigStore.setState({
      imageStyle: data.imageStyle || DEFAULT_IMAGE_STYLE,
      outputFormat: data.outputFormat || DEFAULT_OUTPUT_FORMAT,
    });
    useSocketStore.setState({ storyLoading: false });
  });

  socket.on("essProjectPreview", (data: {
    storyId: string;
    title?: string;
    characters: any[];
    scenes: any[];
    beats: any[];
  }) => {
    usePreviewStore.getState().setProjectPreview({
      storyId: data.storyId,
      title: data.title,
      characters: data.characters,
      scenes: data.scenes,
      beats: data.beats,
    });
  });

  socket.on("essComicPreview", (data: {
    comicId: string;
    storyId: string;
    title: string;
    pages: any[];
  }) => {
    usePreviewStore.getState().setComicPreview({
      comicId: data.comicId,
      storyId: data.storyId,
      title: data.title,
      pages: data.pages,
    });
  });

  socket.on("sharedComicPreview", (data: {
    shareId: string;
    comicId: string;
    title: string;
    pages: any[];
  }) => {
    usePreviewStore.getState().setComicPreview({
      comicId: data.comicId,
      storyId: data.shareId,
      title: data.title,
      pages: data.pages,
    });
  });

  socket.on("essComicDeleted", (data: { comicId: string }) => {
    useESSStore.getState().removeComic(data.comicId);
    confirmDeleted(data.comicId);
  });

  socket.on("essComicDeleteFailed", (data: { comicId: string; error: string }) => {
    recordDeleteFailure(data.comicId, data.error);
  });

  socket.on("essSaved", (_data: { storyId: string }) => {
    socket.emit("listESSProjects", {});
  });

  socket.on("essDeleted", (data: { storyId: string }) => {
    useESSStore.getState().removeProject(data.storyId);
    confirmDeleted(data.storyId);
    // Deleting the active project: clear local stores only. NOT clearStory — the
    // server clears its own session, and clearStory would snapshot a transcript
    // onto the just-deleted storyId.
    if (useStoryStore.getState().storyId === data.storyId) {
      useStoryStore.getState().clearAll();
      useComicStore.getState().clearAll();
    }
  });

  socket.on("essDeleteFailed", (data: { storyId: string; error: string }) => {
    recordDeleteFailure(data.storyId, data.error);
  });

  socket.on("projectTitleUpdated", (data: { storyId: string; title: string }) => {
    const state = useESSStore.getState();
    const now = Date.now();
    const updated = state.projects.map((p) =>
      p.storyId === data.storyId ? { ...p, title: data.title, updatedAt: now } : p,
    );
    useESSStore.setState({ projects: updated });
    const story = useStoryStore.getState();
    if (story.storyId === data.storyId) {
      useStoryStore.setState({ title: data.title });
    }
    if (usePreviewStore.getState().storyId === data.storyId) {
      usePreviewStore.setState({ title: data.title });
    }
  });

  socket.on("essError", (data: { error: string; code?: string }) => {
    console.error("[ESS] Error:", data.code, data.error);
    const isSave = data.error?.includes("save") || data.error?.includes("update");
    minervaService.recordAppEvent(isSave ? AppEvent.ESS_SAVE_FAILURE : AppEvent.ESS_LOAD_FAILURE);
    // A failed load emits no essProjectLoaded/fullState, so clear these here or
    // they stick: storyLoading would withhold content forever, and a leftover
    // autoStartVoice/voiceSuspended would open voice over a project that never
    // loaded. A save failure must not touch them — it's unrelated to load state.
    if (!isSave) {
      useSocketStore.setState({ storyLoading: false, autoStartVoice: false, entryGreeting: null, voiceSuspended: false });
    }
    const preview = usePreviewStore.getState();
    if (preview.isLoading) {
      const message = data.code === "NOT_FOUND" ? STORY_NOT_FOUND_ERROR : GENERIC_ERROR;
      preview.setError(message);
    }
  });
}
